import { LOGIN_METHOD_MAGIC_LINK, LOGIN_METHOD_WINDOW_SECONDS } from '@common/neo.tax';

import { LocalStorageKey } from '@neo.tax/shared/enums';

export const getBooleanFromLocalStorage = (key: string): boolean => localStorage.getItem(key) === 'true';

export const setBooleanInLocalStorage = (key: string, value: boolean): void => localStorage.setItem(key, String(value));

/**
 * Remembers, for the brief audit fresh-login window, that this session signed in via magic link.
 * authLink stamps the x-login-method request header from this so the server's auth guard records
 * MAGIC_LINK — Firebase reports magic-link and email/password logins identically, so the guard needs
 * a client hint. See login-method-header.ts in @common for why this is a header, not a cookie.
 */
export const rememberMagicLinkLogin = (): void => {
  const expiresAt = Date.now() + LOGIN_METHOD_WINDOW_SECONDS * 1000;
  localStorage.setItem(
    LocalStorageKey.SESSION_LOGIN_METHOD,
    JSON.stringify({ method: LOGIN_METHOD_MAGIC_LINK, expiresAt }),
  );
};

/**
 * Returns the remembered login method while still inside the fresh-login window, else null (clearing
 * an expired or corrupt value). Read by authLink to stamp the x-login-method header on every request
 * during the window; the server-side audit dedup collapses those to a single row.
 */
export const getActiveLoginMethod = (): string | null => {
  const raw = localStorage.getItem(LocalStorageKey.SESSION_LOGIN_METHOD);
  if (!raw) {
    return null;
  }
  try {
    const { expiresAt, method } = JSON.parse(raw) as { method: string; expiresAt: number };
    if (typeof expiresAt === 'number' && Date.now() < expiresAt) {
      return method;
    }
  } catch {
    // Corrupt value — fall through to clear it.
  }
  localStorage.removeItem(LocalStorageKey.SESSION_LOGIN_METHOD);
  return null;
};
